import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Store } from '@ngxs/store';
import { filter, take } from 'rxjs/operators';
import { IconNames } from '../../../../../common/constants/icon-names';
import { AutoUnsubscribe } from '../../../../../common';
import { ActionButtonComponent, ButtonAction, ButtonSize } from '../../../../action-button';
import { FilterPopupChipsModel } from '../../../../../store/state/monitoring/models/filter-popup-chips.model';
import { ChipsFilterDialogFacade, chipsFilterDialogFactory } from './mat-chips-filter-dialog.factory';
import { ChipsItemForm, DialogDataModel } from './mat-chips-filter-dialog.model';
import { DirectivesModule } from '../../../../../common/directives/directives.module';
import { SvgIconComponent } from '../../../../svg-icon/svg-icon.component';

@Component({
  selector: 'ui-mat-chips-filter-dialog',
  templateUrl: './mat-chips-filter-dialog.component.html',
  styleUrls: ['./mat-chips-filter-dialog.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatChipsModule,
    TranslateModule,
    DirectivesModule,
    SvgIconComponent,
    ActionButtonComponent,
  ],
  providers: [
    {
      provide: ChipsFilterDialogFacade,
      useFactory: chipsFilterDialogFactory,
      deps: [MAT_DIALOG_DATA, Store],
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@AutoUnsubscribe
export class MatChipsFilterDialogComponent implements OnInit {
  subscriptions: Subscription[] = [];
  buttonAction = ButtonAction;
  buttonSize = ButtonSize;
  formArray: FormArray<FormGroup<ChipsItemForm>> = this.fb.array<FormGroup<ChipsItemForm>>([]);
  numberOfDisplayedItems = 10;
  iconNameList = IconNames;

  constructor(
    public dialogRef: MatDialogRef<MatChipsFilterDialogComponent>,
    public dialogFacade: ChipsFilterDialogFacade,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA)
    public data: DialogDataModel,
  ) {
    this.dialogFacade.load();
  }

  ngOnInit(): void {
    this.subscriptions.push(this.initCodeListForm());
  }

  initCodeListForm(): Subscription {
    return this.dialogFacade.items$
      .pipe(filter(Boolean), take(1))
      .subscribe((items: FilterPopupChipsModel[]) => this.setListForm(items));
  }

  apply(): void {
    const selectedItems = this.formArray.value
      .filter((item: FilterPopupChipsModel) => item.isSelected)
      .map((item: FilterPopupChipsModel) => ({ value: item.value, description: item.description }));

    this.dialogRef.close({ selectedItems });
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  private setListForm(items: FilterPopupChipsModel[]): void {
    const preselectedItems: FilterPopupChipsModel[] = this.data.filtersFormControlOrGroup.value ?? [];
    this.numberOfDisplayedItems = items.length;

    items.forEach((item: FilterPopupChipsModel) => {
      let isSelected = false;
      if (preselectedItems.length) {
        isSelected = !!preselectedItems.find(
          (preselectedItem: FilterPopupChipsModel) => preselectedItem.value === item.value,
        );
      }

      this.formArray.push(
        new FormGroup<ChipsItemForm>({
          isSelected: new FormControl(isSelected),
          value: new FormControl(item.value),
          description: new FormControl(item.description),
        }),
      );
    });
  }
}
